import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import DISCChart from '../../components/DISCChart';
import { getAssessments, deleteAssessment, getDISCQuestions } from '../../utils/api';
import {
  ClipboardList,
  Search,
  Trash2,
  Eye,
  Filter,
  ChevronDown,
  FileText,
  X,
} from 'lucide-react';

// Answer Details Modal Component
const AnswerDetailsModal = ({ assessment, questions, onClose }) => {
  if (!assessment?.answers || !questions?.length) return null;

  const styleColors = {
    D: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
    I: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
    S: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
    C: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  };

  const getOptionText = (question, style) => {
    return question[`option_${style.toLowerCase()}`];
  };

  // Count selections
  const counts = { D: 0, I: 0, S: 0, C: 0 };
  assessment.answers.forEach(a => {
    if (a.most) counts[a.most]++;
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Assessment Answers: {assessment.respondent_first_name} {assessment.respondent_last_name}
            </h2>
            <p className="text-sm text-gray-500">
              Primary Style: <span className={`font-medium ${styleColors[assessment.primary_style]?.text}`}>
                {assessment.style_blend || assessment.primary_style}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Summary */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600 mb-2">MOST Selections:</p>
          <div className="flex gap-4">
            {['D', 'I', 'S', 'C'].map(style => (
              <div key={style} className={`px-3 py-1 rounded-lg ${styleColors[style].bg} ${styleColors[style].text}`}>
                <span className="font-bold">{style}:</span> {counts[style]}
              </div>
            ))}
          </div>
        </div>

        {/* Questions Table */}
        <div className="flex-1 overflow-auto p-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Q#</th>
                <th className="px-3 py-2 text-left font-medium text-red-700">D</th>
                <th className="px-3 py-2 text-left font-medium text-orange-700">I</th>
                <th className="px-3 py-2 text-left font-medium text-blue-700">S</th>
                <th className="px-3 py-2 text-left font-medium text-yellow-700">C</th>
                <th className="px-3 py-2 text-center font-medium text-gray-700">MOST</th>
                <th className="px-3 py-2 text-center font-medium text-gray-700">LEAST</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {questions.map((question, idx) => {
                const answer = assessment.answers[idx] || {};
                return (
                  <tr key={question.id || idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">{idx + 1}</td>
                    <td className={`px-3 py-2 ${answer.most === 'D' ? 'bg-green-100 font-medium' : answer.least === 'D' ? 'bg-red-50 text-gray-400' : ''}`}>
                      {getOptionText(question, 'D')}
                    </td>
                    <td className={`px-3 py-2 ${answer.most === 'I' ? 'bg-green-100 font-medium' : answer.least === 'I' ? 'bg-red-50 text-gray-400' : ''}`}>
                      {getOptionText(question, 'I')}
                    </td>
                    <td className={`px-3 py-2 ${answer.most === 'S' ? 'bg-green-100 font-medium' : answer.least === 'S' ? 'bg-red-50 text-gray-400' : ''}`}>
                      {getOptionText(question, 'S')}
                    </td>
                    <td className={`px-3 py-2 ${answer.most === 'C' ? 'bg-green-100 font-medium' : answer.least === 'C' ? 'bg-red-50 text-gray-400' : ''}`}>
                      {getOptionText(question, 'C')}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-1 rounded font-bold ${styleColors[answer.most]?.bg} ${styleColors[answer.most]?.text}`}>
                        {answer.most}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-block px-2 py-1 rounded bg-gray-200 text-gray-600 font-medium">
                        {answer.least}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const Assessments = () => {
  const [assessments, setAssessments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showAnswersModal, setShowAnswersModal] = useState(false);

  useEffect(() => {
    loadAssessments();
    loadQuestions();
  }, [statusFilter]);

  const loadAssessments = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const response = await getAssessments(params);
      setAssessments(response.data);
    } catch (error) {
      console.error('Error loading assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await getDISCQuestions();
      setQuestions(response.data);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) {
      return;
    }

    try {
      await deleteAssessment(id);
      loadAssessments();
      if (selectedAssessment?.id === id) {
        setSelectedAssessment(null);
      }
    } catch (error) {
      console.error('Error deleting assessment:', error);
    }
  };

  const styleColors = {
    D: 'bg-red-100 text-red-800 border-red-200',
    I: 'bg-orange-100 text-orange-800 border-orange-200',
    S: 'bg-blue-100 text-blue-800 border-blue-200',
    C: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  };

  const filteredAssessments = assessments.filter((a) => {
    const name = `${a.respondent_first_name} ${a.respondent_last_name}`.toLowerCase();
    const email = a.respondent_email?.toLowerCase() || '';
    const search = searchTerm.toLowerCase();
    return name.includes(search) || email.includes(search);
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Assessments</h1>
        <p className="text-gray-600">View and manage completed assessments</p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent appearance-none bg-white"
          >
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assessment List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {filteredAssessments.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filteredAssessments.map((assessment) => (
                  <div
                    key={assessment.id}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedAssessment?.id === assessment.id
                        ? 'bg-tsvmap-orange/5 border-l-4 border-tsvmap-orange'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedAssessment(assessment)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">
                            {assessment.respondent_first_name} {assessment.respondent_last_name}
                          </span>
                          {assessment.primary_style && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                                styleColors[assessment.primary_style]
                              }`}
                            >
                              {assessment.style_blend || assessment.primary_style}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {assessment.respondent_email}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {assessment.organization_name || 'No organization'}
                          {assessment.job_role_title && ` • ${assessment.job_role_title}`}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            assessment.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : assessment.status === 'in_progress'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {assessment.status}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(assessment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No assessments found</p>
              </div>
            )}
          </div>
        </div>

        {/* Assessment Detail */}
        <div className="lg:col-span-1">
          {selectedAssessment ? (
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Assessment Details</h3>
                <div className="flex gap-2">
                  {selectedAssessment.status === 'completed' && selectedAssessment.answers && (
                    <button
                      onClick={() => setShowAnswersModal(true)}
                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                      title="View Answers"
                    >
                      <FileText className="h-5 w-5" />
                    </button>
                  )}
                  {selectedAssessment.status === 'completed' && (
                    <Link
                      to={`/results/${selectedAssessment.uuid}`}
                      target="_blank"
                      className="p-2 text-tsvmap-blue hover:bg-tsvmap-blue/10 rounded-lg"
                      title="View Results"
                    >
                      <Eye className="h-5 w-5" />
                    </Link>
                  )}
                  <button
                    onClick={() => handleDelete(selectedAssessment.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium text-gray-900">
                    {selectedAssessment.respondent_first_name} {selectedAssessment.respondent_last_name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">
                    {selectedAssessment.respondent_email || '-'}
                  </p>
                </div>
                {selectedAssessment.organization_name && (
                  <div>
                    <p className="text-sm text-gray-500">Organization</p>
                    <p className="font-medium text-gray-900">
                      {selectedAssessment.organization_name}
                    </p>
                  </div>
                )}
                {selectedAssessment.job_role_title && (
                  <div>
                    <p className="text-sm text-gray-500">Job Role</p>
                    <p className="font-medium text-gray-900">
                      {selectedAssessment.job_role_title}
                    </p>
                  </div>
                )}

                {selectedAssessment.status === 'completed' && selectedAssessment.primary_style && (
                  <>
                    <hr />
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Personality Style</p>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium border ${
                            styleColors[selectedAssessment.primary_style]
                          }`}
                        >
                          {selectedAssessment.style_blend || selectedAssessment.primary_style}
                        </span>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <DISCChart
                        scores={{
                          D: selectedAssessment.basic_d_score,
                          I: selectedAssessment.basic_i_score,
                          S: selectedAssessment.basic_s_score,
                          C: selectedAssessment.basic_c_score,
                        }}
                        title="Basic Style"
                        size="small"
                      />
                    </div>
                  </>
                )}

                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium text-gray-900">
                    {new Date(selectedAssessment.created_at).toLocaleString()}
                  </p>
                </div>
                {selectedAssessment.completed_at && (
                  <div>
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="font-medium text-gray-900">
                      {new Date(selectedAssessment.completed_at).toLocaleString()}
                    </p>
                  </div>
                )}

                {selectedAssessment.status === 'completed' && selectedAssessment.answers && (
                  <>
                    <hr />
                    <button
                      onClick={() => setShowAnswersModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                    >
                      <FileText className="h-4 w-4" />
                      View Question Answers
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <Eye className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Select an assessment to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Answers Modal */}
      {showAnswersModal && selectedAssessment && (
        <AnswerDetailsModal
          assessment={selectedAssessment}
          questions={questions}
          onClose={() => setShowAnswersModal(false)}
        />
      )}
    </Layout>
  );
};

export default Assessments;
